<template>
  <div class="blogs-listing flex flex-col">
    <div class="w-full h-full fixed left-0 top-0 bg-white bg-opacity-50 z-50" v-if="isLoading">
      <loader :label="$t('frontend_loading')" />
    </div>

    <div class="w-full h-16  flex flex-row justify-between items-center flex-shrink-0">
      <div class="flex justify-between mt-2 mb-3">
        <div class="flex">
          <gd-dropdown :options="blogSortList" class="w-48" v-model="selectedBlogSort" @change="getBlogs"></gd-dropdown>
        </div>
      </div>
      <div class="w-80 ">
        <text-input class="" :placeholder="$t('frontend_grooveblog_search_blog')" v-model="searchBlogName.name"
          @keyup="getBlogs">
          <img src="@/assets/images/icons/search_query.png" slot="prependIcon" />
        </text-input>
      </div>
      <gd-button class="w-auto h-10 px-6" variant="primary" @click="toggleModal(MODAL_OPEN, CREATE_BLOG_MODAL)">{{
        $t('frontend_grooveblog_new_blog') }}</gd-button>
      <gd-modal name="create-blog-modal" :title="$t('frontend_grooveblog_create_blog')">
        <div class="flex flex-col px-8 py-5">
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">{{ $t('frontend_grooveblog_blog_name') }}</label>
            <text-input class="" v-model="blog.name" :validator="$v.blog.name" placeholder="" />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">{{ $t('frontend_grooveblog_subdomain') }}</label>
            <text-input class="" v-model="blog.subdomain" :validator="$v.blog.subdomain" placeholder="" />
            <div class="text-grey-500 mt-1">
              {{ blog.subdomain }}.{{ grooveblogDomain }}
            </div>
            <div class="w-full error text-primary-dark mt-1 text-sm" v-if="domainMessage">
              {{ $t('frontend_grooveblog_subdomain_unavailable') }}
            </div>
          </div>

          <div class="flex flex-col w-full mb-12">
            <label class="text-grey-dark mb-2">
              {{ $t('frontend_groovemember_membership_choose_language') }}
            </label>
            <gd-dropdown class="w-full" v-model="blog.language" :options="languages"></gd-dropdown>
          </div>

          <div class="flex flex-row w-full justify-center">
            <gd-button class="text-center h-10 border-grey-1 mr-4" size="large"
              @click="toggleModal(MODAL_CLOSE, CREATE_BLOG_MODAL)">{{ $t('frontend_cancel') }}</gd-button>
            <gd-button class="text-center h-10" size="large" variant="primary" @click="createBlog">{{ $t('frontend_done')
            }}</gd-button>
          </div>
        </div>
      </gd-modal>
      <gd-modal name="edit-blog-modal" :title="$t('frontend_grooveblog_rename_blog')">
        <div class="flex flex-col px-8 py-5">
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">{{ $t('frontend_grooveblog_blog_name') }}</label>
            <text-input v-model="blog.name" :validator="$v.blog.name" placeholder="" />
          </div>
          <div class="flex flex-row w-full justify-center">
            <gd-button class="text-center h-10 border-grey-1 mr-4" size="large"
              @click="toggleModal(MODAL_CLOSE, EDIT_BLOG_MODAL)">{{ $t('frontend_cancel') }}</gd-button>
            <gd-button class="text-center h-10" size="large" variant="primary" @click="updateBlog">{{
              $t('frontend_update') }}</gd-button>
          </div>
        </div>
      </gd-modal>
      <gd-modal name="clone-blog-modal" :title="$t('frontend_grooveblog_clone_blog')">
        <div class="flex flex-col px-8 py-5">
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">{{ $t('frontend_grooveblog_blog_name') }}</label>
            <text-input class="" v-model="cloneBlog.name" :validator="$v.cloneBlog.name" placeholder="" />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">{{ $t('frontend_grooveblog_subdomain') }}</label>
            <text-input class="" v-model="cloneBlog.subdomain" :validator="$v.cloneBlog.subdomain" placeholder="" />
            <div class="text-grey-500 mt-1">
              {{ cloneBlog.subdomain }}.{{ grooveblogDomain }}
            </div>
            <div class="w-full error text-primary-dark mt-1 text-sm" v-if="cloneDomainMessage">
              {{ $t('frontend_grooveblog_subdomain_unavailable') }}
            </div>
          </div>
          <div class="flex flex-row w-full justify-center">
            <gd-button class="text-center h-10 border-grey-1 mr-4" size="large"
              @click="toggleModal(MODAL_CLOSE, CLONE_BLOG_MODAL)">{{ $t('frontend_cancel') }}</gd-button>
            <gd-button class="text-center h-10" size="large" variant="primary" @click="createCloneBlog">{{
              $t('frontend_done') }}</gd-button>
          </div>
        </div>
      </gd-modal>
    </div>

    <div class="flex flex-wrap py-6 -mx-5 flex-grow content-start">
      <info-panel v-for="blogName in blogList" :key="blogName.id" class="blog-section my-4 mx-5">
        <template slot="content">
          <div class="w-full h-full rounded-t-lg relative">
            <div
              class="absolute w-full h-full rounded-t-lg panel-bg-transparent opacity-0 hover:opacity-100 flex items-center justify-center">
              <gd-button variant="primary" size="large" @click="openBlog(blogName.id)">{{
                $t('frontend_grooveblog_open_blog') }}</gd-button>
            </div>
            <screenshot class="w-full h-full rounded-t-lg bg-center"
              :src="blogName.thumbnail || 'default-screenshot.png'" />
          </div>
        </template>
        <template slot="footer">
          <div class="flex flex-row justify-between px-4 items-center w-full">
            <div class="flex flex-col justify-center">
              <span class=" font-semibold">{{ blogName.name }}</span>
            </div>
            <drop-menu :menu-items="getMenuItems(blogName, blogName.is_live)" small :smallIcon="true"
              variant="base"></drop-menu>
          </div>
        </template>
      </info-panel>
      <gd-modal name="share-blog-modal" width="800px">
        <div class="flex flex-row w-full justify-between pt-8 px-8">
          <div class="text-xl text-grey-700 font-bold h-full flex flex-col justify-center">
            {{ $t('frontend_sites_share_links') }}
          </div>
          <div class="flex flex-row justify-end">
            <gd-button class="mr-2" variant="outline" @click="() => {
              this.$root.$emit('modal-close', 'share-blog-modal');
            }
              ">{{ $t('frontend_sites_integration_modal_exit') }}</gd-button>
            <gd-button @click="addShareLink()" variant="primary">{{
              $t('frontend_sites_integration_modal_add')
            }}</gd-button>
          </div>
        </div>

        <div class="flex flex-col items-center justify-center">
          <div class="flex flex-col w-full justify-center items-center py-8">
            <div v-for="link in activeLinks" :key="link.id" class="flex flex-row justify-between w-full px-8 py-2">
              <div class="flex items-center">
                <gd-button @click="copyShareUrl(fullLink(link.link))" size="icon" variant="primary"
                  :title="$t('frontend_vendors_copy_affiliate_link')">
                  <i class="fas fa-copy" />
                </gd-button>
                <div class="text-grey-600 ml-2">{{ fullLink(link.link) }}</div>
              </div>
              <gd-button @click="updateLinkStatus(link, 0)" variant="outline">{{
                $t('frontend_disable')
              }}</gd-button>
            </div>
          </div>

          <div v-if="inActiveLinks.length > 0"
            class="flex w-full flex-col items-center justify-center border-t border-grey-200">
            <div class="text-lg text-grey-600 font-bold w-full px-8 py-2">
              {{ $t('frontend_sites_disabled_links') }}
            </div>
            <div class="flex flex-col w-full justify-center items-center py-4">
              <div v-for="link in inActiveLinks" :key="link.id"
                class="flex flex-row justify-between w-full px-8 py-2 items-center">
                <div class="text-grey-600">{{ fullLink(link.link) }}</div>
                <div class="flex flex-row">
                  <gd-button class="mr-2" @click="updateLinkStatus(link, 1)">{{
                    $t('frontend_enable')
                  }}</gd-button>
                  <gd-button size="icon" @click="deleteLinkStatus(link)" primary :title="$t('frontend_delete')">
                    <i class="fas fa-trash" />
                  </gd-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </gd-modal>
    </div>
    <upgrade-modal ref="upgradeModal" />
  </div>
</template>

<script>
import axios from 'axios';
import { required } from 'vuelidate/lib/validators';
import Loader from '@groovepages/gd-ui-kit/components/Loader';
import Screenshot from '@groovepages/gd-ui-kit/components/Screenshot';
import Auth from '@groovepages/gd-lib/auth';
import UpgradeModal from '../../shared/ui/UpgradeModal';
import themeConfig from '@/theme/themeConfig.js';


export default {
  middleware: 'auth',
  layout: 'blogList',
  metaInfo() {
    return { title: 'Blogs' };
  },
  components: { Loader, UpgradeModal, Screenshot },
  data() {
    return {
      MODAL_OPEN: 'modal-open',
      MODAL_CLOSE: 'modal-close',
      CREATE_BLOG_MODAL: 'create-blog-modal',
      EDIT_BLOG_MODAL: 'edit-blog-modal',
      CLONE_BLOG_MODAL: 'clone-blog-modal',
      SHARE_BLOG_MODAL: 'share-blog-modal',
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      successMessage: false,
      canLive: true,
      liveBlogsCount: 0,
      blog: {
        name: '',
        subdomain: '',
        language: localStorage.getItem('lang') ?? 'en',
      },
      cloneBlog: { name: '', subdomain: '' },
      activeLinks: [],
      inActiveLinks: [],
      blogIdShareLink: '',
      blogList: [],
      editBlog: {},
      getBlogid: '',
      searchBlogName: { name: '' },
      domainValue: '',
      domainMessage: false,
      cloneDomainValue: '',
      cloneDomainMessage: false,
      cloneId: '',
      languages: [
        { value: 'en', text: this.$t('frontend_english') },
        { value: 'pt', text: this.$t('frontend_portuguese') },
      ],
      selectedBlogSort: "updated_at-desc",
      blogSortList: [
        { "id": 1, "value": "name-asc", "text": "Name(A-Z)" },
        { "id": 2, "value": "name-desc", "text": "Name(Z-A)" },
        { "id": 3, "value": "id-asc", "text": "First Created" },
        { "id": 4, "value": "id-desc", "text": "Last Created" },
        { "id": 5, "value": "updated_at-asc", "text": "First Updated" },
        { "id": 6, "value": "updated_at-desc", "text": "Last Updated" },
      ],
      userFeature: '',
    };
  },
  validations: {
    blog: {
      name: {
        required,
      },
      subdomain: {
        required,
      },
    },
    cloneBlog: {
      name: {
        required,
      },
      subdomain: {
        required,
      },
    },
  },
  computed: {
    grooveblogDomain() {
      return window.gdEnv['VUE_APP_GROOVEBLOG_URL'].match(/\/\/(.+)$/)[1] || 'grooveblog.com'
    }
  },
  async mounted() {

    await this.getCountOfLiveBlogs();
    await this.getBlogs();
    await this.canMakeLive();

    if (this.$route.query.new && this.$route.query.new === 'modal') {
      this.toggleModal(this.MODAL_OPEN, this.CREATE_BLOG_MODAL);
    }
  },
  methods: {
    toggleModal(action, modalName) {
      this.$root.$emit(action, modalName);
    },
    async createBlog() {
      this.$v.blog.$touch();
      if (!this.$v.blog.$invalid) {
        this.$v.blog.$reset();
        this.showLoader();
        await axios
          .get(`/blogs/check-subdomain/${this.blog.subdomain}`)
          .then((response) => {
            this.domainValue = response.data.available;
            this.hideLoader();
          })
          .catch((error) => {
            console.log(error);
          });
        if (this.domainValue) {
          this.showLoader();
          axios
            .post('/blogs', this.blog)
            .then((response) => {
              localStorage.setItem('blog-id', response.data.data.id);
              this.toggleModal(this.MODAL_CLOSE, this.CREATE_BLOG_MODAL);
              this.$router.push({ name: 'home' });
              this.hideLoader();
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          this.domainMessage = true;
        }
      }
    },
    async createCloneBlog() {
      this.$v.cloneBlog.$touch();
      if (!this.$v.cloneBlog.$invalid) {
        this.$v.cloneBlog.$reset();
        this.showLoader();
        await axios
          .get(`/blogs/check-subdomain/${this.cloneBlog.subdomain}`)
          .then((response) => {
            this.cloneDomainValue = response.data.available;
            this.hideLoader();
          })
          .catch((error) => {
            console.log(error);
          });
        if (this.cloneDomainValue) {
          this.showLoader();
          axios
            .post(`/blog/clone/${this.cloneId}`, this.cloneBlog)
            .then((response) => {
              localStorage.setItem('blog-id', response.data.data.id);
              this.toggleModal(this.MODAL_CLOSE, this.CLONE_BLOG_MODAL);
              this.$router.push({ name: 'home' });
              this.hideLoader();
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          this.cloneDomainMessage = true;
        }
      }
    },
    async getBlogs() {
      this.showLoader();

      await axios
        .get('/blogs', {
          params: {
            searchBy: this.searchBlogName.name,
            sortBy: this.selectedBlogSort
          }
        })
        .then((response) => {
          this.blogList = response.data.data;
          this.hideLoader();
        });
    },

    getMenuItems(row, isLive) {
      const items = [
        this.createMenuItem('frontend_builder_pages_rename', () => this.ranameBlog(row.id)),
        this.createMenuItem('frontend_groovemail_forms_index_edit', () => this.openBlog(row.id)),
        this.createMenuItem('frontend_clone', () => this.prepareCloneBlog(row.id)),
        this.createMenuItem('frontend_share', () => this.shareBlog(row)),
        this.createMenuItem('frontend_delete', () => this.deleteBlog(row.id)),
      ];

      const liveBlogText = isLive ? 'frontend_grooveblog_forms_make_draft' : 'frontend_groovemember_course_listing_make_live';
      const liveBlogCallback = () => this.makeLiveBlog(row.id, !isLive);

      if (isLive || this.canLive) {
        items.push(this.createMenuItem(liveBlogText, liveBlogCallback));
      }

      return items;
    },

    createMenuItem(text, callback) {
      return {
        text: this.$t(text),
        callback,
      };
    },

    ranameBlog(id) {
      this.showLoader();
      this.getBlog(id).then(blog => {
        this.blog = blog;
        this.hideLoader();
        this.toggleModal(this.MODAL_OPEN, this.EDIT_BLOG_MODAL);
        this.getBlogid = id;
      });
    },
    prepareCloneBlog(id) {
      this.showLoader();
      this.getBlog(id).then(blog => {
        this.cloneBlog.name = `${blog.name}-clone`;
        this.toggleModal(this.MODAL_OPEN, this.CLONE_BLOG_MODAL);
        this.hideLoader();
      });
      this.cloneId = id;
    },
    deleteBlog(id) {
      this.showLoader();
      axios.delete(`/blogs/${id}`).then(() => {
        this.getBlogs();
        this.hideLoader();
      });
    },

    getBlog(id) {
      return axios.get(`/blogs/${id}`).then(response => response.data.data);
    },

    shareBlog(row) {
      this.activeLinks = [];
      this.activeLinks = row.share_links.filter((link) => link.status === 1);
      this.inActiveLinks = row.share_links.filter((link) => link.status === 0);
      this.blogIdShareLink = row.id;
      this.toggleModal(this.MODAL_OPEN, this.SHARE_BLOG_MODAL);
    },
    fullLink(link) {
      const { grooveappsurl } = window.config;
      const path = process.env.NODE_ENV === 'development' ? `blogs/${link}/share` : `grooveblog/blogs/${link}/share`;
      return `${grooveappsurl}/${path}`;
    },
    async addShareLink() {
      this.showLoader();
      await axios
        .post(`/blogs/add-share-link/${this.blogIdShareLink}`)
        .then((response) => {
          const linkData = response.data.data;
          const blogIndex = this.blogList.findIndex(
            (blog) => blog.id === this.blogIdShareLink
          );
          this.blogList[blogIndex].share_links.push(response);
          this.activeLinks.push(linkData);
        });
      this.hideLoader();
    },
    copyShareUrl(link) {
      this.$copyText(link).then(
        () => {
          this.onCopy('Share link Copied!');
        },
        () => {
          this.onError();
        }
      );
    },
    async updateLinkStatus(row, status) {
      this.showLoader();
      await axios
        .post(`/blogs/${this.blogIdShareLink}/update-share-link/${row.id}`, {
          status,
        })
        .then((response) => {
          return response.data.data;
        });

      if (status === 1) {
        /** Make it Active */
        const linkIndex = this.inActiveLinks.findIndex(
          (link) => link.id === row.id
        );
        this.inActiveLinks.splice(linkIndex, 1);
        this.activeLinks.push(row);
      } else {
        /** Make it Inactive */
        const linkIndex = this.activeLinks.findIndex(
          (link) => link.id === row.id
        );
        this.activeLinks.splice(linkIndex, 1);
        this.inActiveLinks.push(row);
      }
      this.hideLoader();
    },
    async deleteLinkStatus(row) {
      this.showLoader();
      await axios
        .post(`/blogs/${this.blogIdShareLink}/delete-share-link/${row.id}`)
        .then(() => {
          const linkIndex = this.inActiveLinks.findIndex(
            (link) => link.id === row.id
          );
          this.inActiveLinks.splice(linkIndex, 1);
        });
      this.hideLoader();
    },
    async getCountOfLiveBlogs() {
      await axios
        .get('/count-live-blogs')
        .then((response) => {
          this.liveBlogsCount = response.data;

        });
    },
    async updateBlog() {
      this.showLoader();
      await axios
        .put(`/blogs/${this.getBlogid}`, {
          name: this.blog.name,
        })
        .then(() => {
          this.getBlogs();
        });
      this.blog = {};
      this.toggleModal(this.MODAL_CLOSE, this.EDIT_BLOG_MODAL);
    },
    async makeLiveBlog(id, live) {
      this.showLoader();
      await axios
        .put(`/blogs/${id}`, {
          live,
        })
        .then(() => {
          this.getBlogs();
        });
      this.openBlog(id);
    },

    async canMakeLive() {
      this.userFeature = Auth.fetchFeatureValue('grooveblog_number_of_blogs');
      if (this.userFeature - this.liveBlogsCount !== 0) {
        this.canLive = true;
      } else {
        this.canLive = false;
      }
    },
    openBlog(id) {
      localStorage.setItem('blog-id', id);
      this.$router.push({ name: 'home' });
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>
<style lang="scss">
.blogs-listing {
  .selectbox {
    .border-solid.cursor-pointer {
      font-size: 14px;
      height: 40px;
      background-color: var(--brand-primary-invert);
      border-color: #d5d8e5;
      border-radius: 6px;

      .select-none {
        padding: 0 12px;
        color: #000;
      }
    }

    svg {
      color: #191d3a;
      height: 20px;
      top: 2px;
      pointer-events: none;
    }

    .italic.select-none {
      display: none;
    }
  }
}
</style>
<style lang="scss" scoped>
/**
  @TODO: Figure it out where these contents belongs
 */

.content-start:empty:before {
  content: 'Create your first Blog';
}

.content-start:empty:after {
  content: 'Build yourself a new Blog, tags & posts. Its easy and we made lots of predefined themes to save you the hard work';
}
</style>
<style>
html {
  line-height: 1.25;
}

.text-grey-dark {
  color: #394b56;
}

.ps-container {
  background: #f7f6fa;
}

.border-grey-1,
button.border-grey-1 {
  border-color: #d5d8e5;
}

.bg-primary-extra-dark.tracking-wide {
  display: none;
}

.custom-input-text {
  height: auto !important;
  border: none !important;
}

.ps-container {
  background: #f7f6fa;
}

.border-grey-1 {
  border-color: #d5d8e5;
}

.text-grey-darkest {
  color: #0f1824;
}

button.text-white {
  color: #ffffff;
}

button.text-primary-dark {
  color: var(--brand-primary-dark);
}

button.text-dark {
  color: #191d3a;
}

button:focus {
  outline: 0 !important;
}


.app-tiles-container {
  max-width: 1260px;
  margin-left: auto;
  margin-right: auto;
}

.custom-last-selectbox .overflow-auto.custom-scrollbars {
  position: absolute;
  bottom: 100% !important;
}

.panel-bg-transparent {
  background: rgba(41, 43, 73, 0.5);
}

.blogs-listing .blog-section {
  width: 320px;
  height: 260px;
}

@media only screen and (max-width: 1769px) {
  .blogs-listing {
    width: 1042px;
    margin: 0 auto;
  }
}

@media only screen and (min-width: 2100px) {
  .blogs-listing .blog-section {
    width: 370px;
    height: 301px;
  }

}
</style>
